import { useEffect } from "react";

/**
 * SEO — per-route <title>, meta description, canonical, OG/Twitter updates.
 *
 * Uses plain DOM (no react-helmet dependency). Restores previous values on
 * unmount so navigations don't leak stale meta between pages.
 */
const ORIGIN = "https://axiony.co.in";
const SITE = "Axiony";
const DEFAULT_DESC = "Hire on signal, not on résumés. Real-time AI match scoring for every applicant, powered by Gemini 3 Flash.";
const DEFAULT_IMAGE = "https://axiony.co.in/og-image.png";

function upsertMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    const [key, val] = selector.replace('meta[', '').replace(']', '').split('=');
    el.setAttribute(key, val.replace(/"/g, ''));
    document.head.appendChild(el);
  }
  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return { el, prev, created };
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, prev, created };
}

export const SEO = ({ title, description, path = "/", noIndex = false, image }) => {
  useEffect(() => {
    const fullTitle = title ? `${title} · ${SITE}` : `${SITE} — LLM-driven talent match platform`;
    const url = `${ORIGIN}${path.startsWith("/") ? path : "/" + path}`.replace(/\/+$/, "") || ORIGIN;
    const desc = description || DEFAULT_DESC;
    const img = image || DEFAULT_IMAGE;

    const prevTitle = document.title;
    document.title = fullTitle;

    const updates = [
      upsertMeta('meta[name="description"]', "content", desc),
      upsertMeta('meta[property="og:title"]', "content", fullTitle),
      upsertMeta('meta[property="og:description"]', "content", desc),
      upsertMeta('meta[property="og:url"]', "content", url),
      upsertMeta('meta[property="og:image"]', "content", img),
      upsertMeta('meta[name="twitter:title"]', "content", fullTitle),
      upsertMeta('meta[name="twitter:description"]', "content", desc),
      upsertMeta('meta[name="twitter:image"]', "content", img),
      upsertLink("canonical", url),
    ];

    let robotsUpdate = null;
    if (noIndex) {
      robotsUpdate = upsertMeta('meta[name="robots"]', "content", "noindex, nofollow");
    }

    return () => {
      document.title = prevTitle;
      updates.forEach((u) => {
        if (u.created) {
          u.el.remove();
        } else if (u.prev !== null) {
          const attr = u.el.tagName === "LINK" ? "href" : "content";
          u.el.setAttribute(attr, u.prev);
        }
      });
      if (robotsUpdate) {
        if (robotsUpdate.created) robotsUpdate.el.remove();
        else if (robotsUpdate.prev !== null) robotsUpdate.el.setAttribute("content", robotsUpdate.prev);
      }
    };
  }, [title, description, path, noIndex, image]);

  return null;
};
